import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { projectAPI } from '@/lib/api/projects'
import { getApiErrorMessage } from '@/lib/api/errors'

interface AddActivityDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: string
  onCreated: () => void
}

export function AddActivityDialog({
  open,
  onOpenChange,
  projectId,
  onCreated,
}: AddActivityDialogProps) {
  const [name, setName] = useState('')
  const [unit, setUnit] = useState('unit')
  const [location, setLocation] = useState('GLO')
  const [referenceProduct, setReferenceProduct] = useState('')
  const [creating, setCreating] = useState(false)

  const resetForm = () => {
    setName('')
    setUnit('unit')
    setLocation('GLO')
    setReferenceProduct('')
  }

  const handleCreate = async () => {
    const trimmedName = name.trim()
    if (!trimmedName) {
      toast.error('Activity name is required')
      return
    }

    setCreating(true)
    try {
      const code = crypto.randomUUID()
      await projectAPI.createActivity({
        project_id: projectId,
        code,
        name: trimmedName,
        unit: unit.trim() || 'unit',
        location: location.trim() || 'GLO',
        reference_product: referenceProduct.trim() || undefined,
      })
      toast.success(`Activity "${trimmedName}" created`)
      resetForm()
      onOpenChange(false)
      onCreated()
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to create activity'))
    } finally {
      setCreating(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!creating) {
          onOpenChange(v)
          if (!v) resetForm()
        }
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-sm">Add Activity</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="act-name" className="text-xs">
              Name *
            </Label>
            <Input
              id="act-name"
              placeholder="e.g. My custom process"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-8 text-xs"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="act-unit" className="text-xs">
                Unit
              </Label>
              <Input
                id="act-unit"
                placeholder="kg, kWh, m3..."
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                className="h-8 text-xs"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="act-location" className="text-xs">
                Location
              </Label>
              <Input
                id="act-location"
                placeholder="GLO, US, DE..."
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="h-8 text-xs"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="act-refprod" className="text-xs">
              Reference Product
            </Label>
            <Input
              id="act-refprod"
              placeholder="Optional"
              value={referenceProduct}
              onChange={(e) => setReferenceProduct(e.target.value)}
              className="h-8 text-xs"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              onOpenChange(false)
              resetForm()
            }}
            disabled={creating}
          >
            Cancel
          </Button>
          <Button size="sm" onClick={handleCreate} disabled={creating || !name.trim()}>
            {creating && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
            Create Activity
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
